import React, { useEffect, useState } from "react";
import { Building2, CheckCircle2, Facebook, Globe, Instagram, Linkedin, MapPin, Phone, Save, Users } from "lucide-react";
import api from "@/lib/api";
import { AgencyLogoUpload } from "@/components/agency/AgencyLogoUpload";
import { AgencyTeamEditor } from "@/components/agency/AgencyTeamEditor";

const inputCls = "w-full bg-transparent border border-white/15 px-4 py-2.5 text-ink-50 focus:border-gold outline-none text-sm transition-colors";
const emptyForm = {
    agency_name: "", agency_description: "", agency_logo: "", agency_phone: "", agency_address: "", agency_website: "", agency_team: [],
    social_instagram: "", social_facebook: "", social_linkedin: "",
};

const fromUser = (user) => ({
    ...emptyForm,
    agency_name: user?.agency_name || "",
    agency_description: user?.agency_description || "",
    agency_logo: user?.agency_logo || "",
    agency_phone: user?.agency_phone || "",
    agency_address: user?.agency_address || "",
    agency_website: user?.agency_website || "",
    agency_team: Array.isArray(user?.agency_team) ? user.agency_team : [],
    social_instagram: user?.social_instagram || "",
    social_facebook: user?.social_facebook || "",
    social_linkedin: user?.social_linkedin || "",
});

const GroupTitle = ({ icon, title, description, testid }) => (
    <div data-testid={testid} className="flex items-start gap-3">
        <span className="w-10 h-10 shrink-0 grid place-items-center border border-gold/25 text-gold">{icon}</span>
        <div><h3 className="font-serif text-xl text-ink-50">{title}</h3><p className="text-xs text-ink-500 mt-1">{description}</p></div>
    </div>
);

const Field = ({ label, children }) => <label className="block"><span className="text-[10px] uppercase tracking-wider text-ink-400 mb-1.5 block">{label}</span>{children}</label>;

export const PublicAgencyProfileEditor = ({ user, onSaved, testPrefix = "agency-profile-editor", className = "" }) => {
    const [form, setForm] = useState(() => fromUser(user));
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => { setForm(fromUser(user)); }, [user]);

    const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
    const save = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const payload = { ...form };
            delete payload.agency_logo;
            const { data } = await api.put("/auth/profile", payload);
            setForm(fromUser(data.user));
            setMessage({ type: "ok", text: "Profilo pubblico aggiornato" });
            await onSaved?.();
        } catch (error) {
            setMessage({ type: "error", text: error?.response?.data?.detail || "Salvataggio non riuscito" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <section data-testid={`${testPrefix}-section`} className={`space-y-8 ${className}`}>
            <div>
                <span className="label-eyebrow">Presenza pubblica</span>
                <h2 data-testid={`${testPrefix}-title`} className="font-serif text-2xl sm:text-3xl text-ink-50 mt-2">Profilo pubblico agenzia</h2>
                <p className="text-sm text-ink-400 mt-2">Compila solo ciò che vuoi mostrare su VendiDirect. Tutti i campi sono opzionali.</p>
            </div>

            <div className="space-y-5">
                <GroupTitle testid={`${testPrefix}-identity-title`} icon={<Building2 size={17} />} title="Identità agenzia" description="Nome, presentazione e logo mostrati nella testata pubblica." />
                <div className="grid grid-cols-1 gap-4">
                    <Field label="Nome agenzia (opzionale)"><input data-testid={`${testPrefix}-agency-name`} className={inputCls} value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} /></Field>
                    <Field label="Descrizione pubblica (opzionale)"><textarea data-testid={`${testPrefix}-agency-description`} rows={3} className={inputCls} value={form.agency_description} onChange={(e) => set("agency_description", e.target.value)} /></Field>
                </div>
                <AgencyLogoUpload value={form.agency_logo} onChange={(value) => set("agency_logo", value)} testPrefix={testPrefix} />
            </div>

            <div className="pt-7 border-t border-white/10 space-y-5">
                <GroupTitle testid={`${testPrefix}-contacts-title`} icon={<MapPin size={17} />} title="Contatti pubblici" description="Indirizzo, telefono e sito esterno; se vuoti non vengono mostrati." />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Indirizzo fisico pubblico (opzionale)"><input data-testid={`${testPrefix}-agency-address`} className={inputCls} placeholder="Via, CAP e città" value={form.agency_address} onChange={(e) => set("agency_address", e.target.value)} /></Field>
                    <Field label="Telefono pubblico (opzionale)"><div className="relative"><Phone size={14} className="absolute left-3 top-3 text-ink-500" /><input data-testid={`${testPrefix}-agency-phone`} className={`${inputCls} pl-9`} value={form.agency_phone} onChange={(e) => set("agency_phone", e.target.value)} /></div></Field>
                    <Field label="Sito web (opzionale)"><div className="relative"><Globe size={14} className="absolute left-3 top-3 text-ink-500" /><input data-testid={`${testPrefix}-agency-website`} type="url" className={`${inputCls} pl-9`} placeholder="https://www.tuosito.it" value={form.agency_website} onChange={(e) => set("agency_website", e.target.value)} /></div></Field>
                </div>
            </div>

            <div className="pt-2">
                <GroupTitle testid={`${testPrefix}-team-title`} icon={<Users size={17} />} title="Team pubblico" description="Foto, nome, cognome, ruolo ed email possono essere inseriti liberamente." />
                <AgencyTeamEditor members={form.agency_team} onChange={(value) => set("agency_team", value)} testPrefix={testPrefix} />
            </div>

            <div className="pt-7 border-t border-white/10 space-y-5">
                <GroupTitle testid={`${testPrefix}-social-title`} icon={<Instagram size={17} />} title="Profili social" description="Aggiungi i link che vuoi rendere cliccabili sul profilo pubblico." />
                <div className="grid grid-cols-1 gap-3">
                    <SocialField icon={<Instagram size={15} />} label="Instagram" testid={`${testPrefix}-social-instagram`} value={form.social_instagram} onChange={(value) => set("social_instagram", value)} />
                    <SocialField icon={<Facebook size={15} />} label="Facebook" testid={`${testPrefix}-social-facebook`} value={form.social_facebook} onChange={(value) => set("social_facebook", value)} />
                    <SocialField icon={<Linkedin size={15} />} label="LinkedIn" testid={`${testPrefix}-social-linkedin`} value={form.social_linkedin} onChange={(value) => set("social_linkedin", value)} />
                </div>
            </div>

            {message && <div data-testid={`${testPrefix}-save-message`} className={`text-xs px-3 py-2 border flex items-center gap-2 ${message.type === "ok" ? "text-green-300 border-green-700/40 bg-green-900/20" : "text-red-300 border-red-700/40 bg-red-900/20"}`}><CheckCircle2 size={13} />{message.text}</div>}
            <div className="flex justify-end">
                <button data-testid={`${testPrefix}-save-button`} type="button" onClick={save} disabled={busy} className="cta-gold px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"><Save size={14} />{busy ? "Salvataggio…" : "Salva profilo pubblico"}</button>
            </div>
        </section>
    );
};

const SocialField = ({ icon, label, testid, value, onChange }) => (
    <label className="flex items-center gap-3"><span className="text-gold shrink-0">{icon}</span><span className="sr-only">{label}</span><input data-testid={testid} type="url" className={inputCls} placeholder={`${label} (opzionale)`} value={value} onChange={(e) => onChange(e.target.value)} /></label>
);